import React, { useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PaginatedTable, PaginatedTableCallbacks, InsufficientPermissions } from '../../common'
import { UnknownSupplierTable } from './table'
import { JsonRecord, Record } from '../../../models'
import { getRecordsWithUnknownSupplier } from '../../../api'
import { getAuthUser } from '../../../helpers';
import defineAbilityFor from '../defineAbility'
import { RecordStatusFilters } from '../filters'
import { AdvancedSearch } from '../advancedSearch'
import { downloadUnknownSupplierCsv } from '../../../api/routes'

const defaultSearchState = {
    rec: '',
    supplierName: '',
    recPhone: '',
    agencyId: ''
}

const advancedSearchKeys = ['rec', 'supplierName', 'recPhone', 'agencyId']
export const UnknownSupplier = () => {
    const ability = defineAbilityFor(getAuthUser())
    const [searchParams, setSearchParams] = useSearchParams()
    const updateFiltersRef = useRef<{
        (
            filterName: string,
            newValue: string,
            shouldDelete?: boolean,
            dependentFilterToClear?: string
        ): void
    } | null>(null)
    const searchInputs = [
        { id: 'rec', name: 'rec', placeholder: 'Record ID', type: "text"},
        { id: 'supplierName', name: 'supplierName', placeholder: 'Supplier Name', type: "text"},
        { id: 'recPhone', name: 'recPhone', placeholder: 'Supplier Phone', type: "text"},
        { id: 'agencyId', name: 'agencyId', placeholder: 'Agency ID', type: 'text'}
    ]

    if (!ability.can('View', 'UnknownSupplier')) {
        return <InsufficientPermissions />
    }

    const handleClear = useCallback(() => {
        const params = new URLSearchParams(searchParams)

        advancedSearchKeys.forEach((key) => params.delete(key))

        params.delete('record_status_code')
        params.delete('record_substatus_code')

        setSearchParams(params, { replace: true })

        updateFiltersRef.current?.(
            'record_status_code',
            '',
            true,
            'record_substatus_code'
        )
    }, [searchParams, setSearchParams])

    return (
        <PaginatedTable<Record, JsonRecord>
            getData={getRecordsWithUnknownSupplier}
            Filters={(props) => {
                updateFiltersRef.current = props.updateFilters;
                return <RecordStatusFilters {...props} />
            }}
            DownloadButton={ability.can('DownloadCsv', 'UnknownSupplierRecord')
                ? (() => (
                    <a
                        href={downloadUnknownSupplierCsv}
                        className="btn btn-primary mt-4"
                        download="unknown_supplier_records.csv"
                    >
                        Download CSV
                    </a>
                ))
                : undefined
            }
            AdvancedSearchComponent={AdvancedSearch}
            advancedSearchProps={{
                inputs: searchInputs,
                defaultState: defaultSearchState,
                onClear: handleClear,
            }}
            defaultAdvancedSearchState={defaultSearchState}
            modelConstructor={(json: JsonRecord) => new Record(json)}
            disableSearch={true}
            Table={(records: Record[], callbacks: PaginatedTableCallbacks) => (
                <UnknownSupplierTable
                    callbacks={callbacks}
                    resources={{records}} />
            )}
            title='Records with Unknown Supplier' />
    )
}
